// Eventreach form adapter and controller
//
// On an event edit page, this will attach to the form and monitor changes,
// triggering score updates. This also is responsible for initializing the
// on-page react list component.

import $ from 'jquery';
import _ from 'underscore';
import React from 'react';
import ReactDOM from 'react-dom';

import { EventReachComponent } from './component';
import { EventEmitter } from '../event_emitter';
import { EventReachEngine as Engine } from './engine';

// EventReach data source from an event edit form
//
// Emits an event on (form) change, causing the react component to update
export class EventReachForm extends EventEmitter {
  // jQuery element for the form we attach to
  form = null;

  // current event data, in normalized form for the rules engine
  event = {};

  // Eventreach engine
  engine = null;

  // Eventreach result
  result = null;

  // Event visibility is public
  visible = true;

  // Event is included in trending
  can_trend = true;

  constructor(options) {
    super();
    this._attachEvents = this._attachEvents.bind(this);
    this._initializeEngine = this._initializeEngine.bind(this);
    this._initializeReact = this._initializeReact.bind(this);
    this._onChange = this._onChange.bind(this);
    this._parseForm = this._parseForm.bind(this);

    this.form = $(options.form);
    this.eventreach_container = $(options.status_container);
    this.platform_config = options.platform_config;

    this._parseForm();
    this._initializeEngine();
    this._initializeReact();
    this._attachEvents();
  }

  // Attach events to the form inputs
  _attachEvents() {
    this.form.on('change.eventreach ifChanged.eventreach', 'input, select, textarea', this._onChange);
    this.form.on('slzr:instance:add.eventreach slzr:instance:change.eventreach slzr:instance:remove.eventreach', ev => _.defer(this._onChange, ev));
    this.form.on('slzr:filter:add.eventreach slzr:filter:remove.eventreach', this._onChange);
    this.form.on('slzr:photo:remove.eventreach slzr:photo:change.eventreach', this._onChange);
    this.form.on('slzr:location:change.eventreach slzr:location:remove.eventreach', this._onChange);
    this.form.on('slzr:item:add.eventreach slzr:item:remove.eventreach', this._onChange);
    return this.form.on('slzr:date:change', this._onChange);
  }

  // Initialize eventreach engine
  _initializeEngine() {
    this.engine = new Engine(this.platform_config);
    return this.result = this.engine.evaluate(this.event);
  }

  // Initialize react component
  _initializeReact() {
    const event_reach_component = React.createElement(EventReachComponent, {eventReach: this});
    return ReactDOM.render(event_reach_component, this.eventreach_container.get(0));
  }

  // Form changed
  _onChange(ev) {
    return _.defer(() => {
      this._parseForm();
      this.result = this.engine.evaluate(this.event);
      return this.emit('change');
    });
  }

  // Parse form into the internal state EventReach needs
  _parseForm() {
    const data = this.form.serializeArray();

    // Convert the array into an object
    //
    // If the key ends in [], treat it as an array value, otherwise
    // a single value.
    const values = {};
    for (const field of Array.from(data)) {
      const field_value = field.value;

      if (field.name.match(/\[\]$/)) {
        if (!values[field.name]) { values[field.name] = []; }
        values[field.name].push(field_value);
      } else {
        values[field.name] = field_value;
      }
    }

    const event = {
      id: values._event_id,
      created_at: values.created_at !== '' ? new Date(values.created_at) : undefined,
      filters: {}
    };

    // Extract basic input fields
    for (const key in values) {
      var m;
      const value = values[key];
      if (m = key.match(/^event\[([a-z0-9_-]+)\]/i)) {
        // Rename fields
        const attribute_name = (() => { switch (m[1]) {
          case 'ticket_info': return 'ticket_url';
          case 'keyword_list': return 'keywords';
          case 'tag_list': return 'tags';
          default: return m[1];
        } })();

        event[attribute_name] = value;
      }

      // Add selected tags that aren't blank
      if (m = key.match(/^tags\[([a-z0-9_-]+)\]/i)) {
        const context = m[1];
        event.filters[context] = _.select(value, i => i !== '');
      }
    }

    // Fix boolean values
    event.allows_reviews = event.allows_reviews === '1';
    event.allows_attendance = event.allows_attendance === '1';
    event.is_sponsored = event.is_sponsored === '1';
    event.hide_from_main_calendar = event.hide_from_main_calendar === '1';
    event.visible_only_to_widget = event.visible_only_to_widget === '1';
    event.visible_only_to_channel = event.visible_only_to_channel === '1';
    event.included_in_trending = event.exclude_from_trending !== '1';

    // Add instance dates, filters, featured sections
    if (values.location_is === 'venue') {
      event.business_id = values.business_email;
      event.location = values.location;
    } else if (values.location_is === 'other') {
      event.business_id = null;
      event.location = values.location;
    }

    event.has_photo = (values.photo_key !== '') && (values.photo_key !== 'deleted');
    if (!values["scheduled_date[]"]) { values["scheduled_date[]"] = []; }
    event.instance_dates = (Array.from(values["scheduled_date[]"]).map((d) => new Date(d.split(/,/)[0])));
    event.featured_sections = values['featured_section_ids[]'];

    // And the date input
    const date_input = this.form.find('[data-instance="start-date"]').data();
    if ((date_input != null ? date_input.dateStatus : undefined) === 'ok') {
      event.instance_dates.push(date_input.date);
    }

    this.event = event;
    this.visible = (event.visibility === 'visible') && !event.hide_from_main_calendar && !event.visible_only_to_widget && !event.visible_only_to_channel;
    return this.can_trend = (event.exclude_from_trending !== '1');
  }
}
